import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  LOCAL_STORAGE_KEY_INPUT,
  LOCAL_STORAGE_KEY_INPUT_MENTIONS,
} from 'constants/localStorageKeys';
import { MessageHeaderFormatsTemplate } from 'store/reducers/templates';
import { ContatoMencionado } from 'types';
import { FILE_SIZE_ERROR_MESSAGES, MAX_FILE_SIZES } from 'utils/validators';

export const MAX_FILES = 10;

export type FileWithCaption = {
  file: File;
  caption: string;
  contatosMencionados?: ContatoMencionado[];
};

type FileManagerProps = {
  maxFiles?: number;
  sessionClosed: boolean;
  localStorageKeyInput?: string;
  localStorageKeyInputMentions?: string;
};

export function useFileManager({
  maxFiles = MAX_FILES,
  sessionClosed,
  localStorageKeyInput = LOCAL_STORAGE_KEY_INPUT,
  localStorageKeyInputMentions = LOCAL_STORAGE_KEY_INPUT_MENTIONS,
}: FileManagerProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [files, setFiles] = useState<FileWithCaption[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const isLimitReached = useMemo(
    () => files.length >= maxFiles,
    [files.length, maxFiles],
  );

  const updateCaption = useCallback(
    (
      index: number,
      caption: string,
      contatosMencionados?: ContatoMencionado[],
    ) => {
      setFiles(prev =>
        prev.map((f, i) =>
          i === index ? { ...f, caption, contatosMencionados } : f,
        ),
      );
    },
    [],
  );

  const removeFile = useCallback(
    (index: number) => {
      setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    },
    [setFiles],
  );

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const caption = localStorage.getItem(localStorageKeyInput);
      const mentionsStr = localStorage.getItem(localStorageKeyInputMentions);
      const contatosMencionados = mentionsStr
        ? (JSON.parse(mentionsStr) as ContatoMencionado[])
        : undefined;

      setFiles(prevFiles => {
        let merged = [...prevFiles];

        const uniqueNewFiles = newFiles
          .filter(f => {
            const alreadyExists = merged.some(
              existing =>
                existing.file.name === f.name &&
                existing.file.size === f.size &&
                existing.file.lastModified === f.lastModified,
            );

            if (alreadyExists) {
              setTimeout(() => {
                enqueueSnackbar(`${f.name} já foi adicionado.`);
              }, 0);
              return false;
            }

            // imagens têm limite próprio; demais arquivos seguem o limite de documento
            const sizeType = f.type.startsWith('image/')
              ? MessageHeaderFormatsTemplate.IMAGEM
              : MessageHeaderFormatsTemplate.DOCUMENTO;

            if (f.size > MAX_FILE_SIZES[sizeType]) {
              setTimeout(() => {
                enqueueSnackbar(FILE_SIZE_ERROR_MESSAGES[sizeType]);
              }, 0);
              return false;
            }

            return true;
          })
          .map((f, index) => {
            // Só seta caption se for:
            // - o primeiro arquivo de TODOS (prevFiles.length === 0)
            // - o primeiro da leva (index === 0)
            // - não for áudio
            if (
              prevFiles.length === 0 &&
              index === 0 &&
              caption &&
              !f.type.startsWith('audio/')
            ) {
              return { file: f, caption, contatosMencionados };
            }
            return { file: f, caption: '' };
          });

        if (merged.length >= maxFiles) {
          setTimeout(() => {
            enqueueSnackbar(
              `Selecione e envie no máximo ${maxFiles} arquivos por vez.`,
            );
          }, 0);
          return merged;
        }

        if (merged.length + uniqueNewFiles.length > maxFiles) {
          setTimeout(() => {
            enqueueSnackbar(
              `Selecione e envie no máximo ${maxFiles} arquivos por vez.`,
            );
          }, 0);
          uniqueNewFiles.splice(maxFiles - merged.length);
        }

        if (uniqueNewFiles.length > 0) {
          const newIndex = merged.length;
          setTimeout(() => {
            setSelectedIndex(newIndex);
          }, 0);
        }

        merged = [...merged, ...uniqueNewFiles];

        return merged;
      });
    },
    [
      enqueueSnackbar,
      maxFiles,
      setSelectedIndex,
      localStorageKeyInput,
      localStorageKeyInputMentions,
    ],
  );

  const handleNextFile = useCallback(() => {
    if (files.length === 0) return;
    setSelectedIndex(prev => (prev + 1) % files.length);
  }, [files.length]);

  const handlePrevFile = useCallback(() => {
    if (files.length === 0) return;
    setSelectedIndex(prev => (prev - 1 + files.length) % files.length);
  }, [files.length]);

  const resetCaption = useCallback(
    (index: number) => {
      setFiles(prevFiles =>
        prevFiles.map((fileWithCaption, i) =>
          i === index
            ? { ...fileWithCaption, caption: '', contatosMencionados: [] }
            : fileWithCaption,
        ),
      );
    },
    [setFiles],
  );

  useEffect(() => {
    if (files.length === 0) setSelectedIndex(0);
    else if (selectedIndex >= files.length) setSelectedIndex(files.length - 1);
  }, [files, selectedIndex]);

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      if (sessionClosed) return;

      if (!event.clipboardData) return;

      const pastedFiles: File[] = Array.from(event.clipboardData.files || []);

      if (pastedFiles.length > 0) {
        event.preventDefault();
        addFiles(pastedFiles);
      }
    };

    document.addEventListener('paste', handlePaste, false);
    return () => document.removeEventListener('paste', handlePaste);
  }, [addFiles, sessionClosed]);

  const fileUrl = useCallback((file: File) => URL.createObjectURL(file), []);

  const reset = useCallback(() => {
    setFiles([]);
    setSelectedIndex(0);
  }, []);

  return {
    files,
    setFiles,
    selectedIndex,
    setSelectedIndex,
    addFiles,
    handleNextFile,
    handlePrevFile,
    fileUrl,
    isLimitReached,
    reset,
    removeFile,
    resetCaption,
    updateCaption,
  };
}
